import styles from './styles.module.css';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DataTable from 'components/utils/DataTable';
import Info from 'components/utils/Alert/Info';
import { Button, Grid } from '@mui/material';
import { ROUTES } from 'const';
import { useHistory } from 'react-router-dom';
import { ShopContext } from 'Context/ShopContext';
import ShopsHelper from 'helpers/ShopsHelper';
import Loader from 'components/utils/Loading';
import BillSummaryReportService from 'services/BillSummaryReportService';
import DateTimeHelpers from 'helpers/DateTimeHelpers';

export default function BillDetailsDialog(props) {
  const { t: tCommon } = useTranslation('common');
  const { t: tPopup } = useTranslation('popup');
  const { shop } = useContext(ShopContext);
  const { shouldShowProfit = false } = props;
  const handleClose = props.handleClose || props.onClose;
  const selectedSale = props.selectedSale || props.bill;

  const [isLoading, setIsLoading] = useState(false);
  const [saleDetails, setSaleDetails] = useState({});

  const history = useHistory();

  const tableHeaders = [
    {
      id: 'item.plu',
      label: tCommon('PLU'),
      type: 'text',
    },
    {
      id: 'item.name',
      label: tCommon('Name'),
      type: 'callback',
      viewRender: (row) => {
        const itemName = row?.item?.name;
        const description = row?.description;

        if (!itemName && description) {
          return <span>{description}</span>;
        }

        return (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span>{itemName || '-'}</span>
            {description && (
              <span
                style={{
                  fontSize: '11px',
                  color: '#64748b',
                  marginTop: '2px',
                  fontStyle: 'italic',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {description}
              </span>
            )}
          </div>
        );
      },
    },
    {
      id: 'quantity',
      label: tCommon('Quantity'),
      type: 'floatQuantity',
    },
    {
      id: 'rate',
      label: tCommon('Rate'),
      type: 'floatAmount',
    },
    {
      id: 'tax',
      label: tCommon('Tax Amount'),
      type: 'text',
    },
    {
      id: 'payable',
      label: tCommon('Total'),
      type: 'floatAmount',
    },
  ];

  if (shouldShowProfit) {
    tableHeaders.push({
      id: 'profit',
      label: tCommon('Profit'),
      type: 'floatAmount',
    });
  }

  const redirectToCustomerrDetails = (customer) => {
    history.push(ROUTES.CUSTOMER_DETAILS.replace(':customerId', customer.id));
  };

  const _getInvoiceSummary = (items) => {
    let totalPrice = 0;
    let totalTax = 0;
    let totalAmount = 0;
    let totalProfit = 0;
    (items || []).forEach((item) => {
      totalPrice += item.rate;
      totalTax += item.tax + item.cess || 0;
      totalAmount += item.payable;
      totalProfit += item.profit || 0;
    });
    return {
      totalPrice: ShopsHelper.getAmountFormatted(shop, totalPrice),
      totalAmount: ShopsHelper.getAmountFormatted(shop, totalAmount),
      totalTax: ShopsHelper.getAmountFormatted(shop, totalTax),
      totalProfit: ShopsHelper.getAmountFormatted(shop, totalProfit),
    };
  };

  const summary = _getInvoiceSummary(saleDetails?.items);

  useEffect(() => {
    const fetchSaleDetails = async () => {
      if (!selectedSale) return;
      const billNo = selectedSale['bill_no'] || selectedSale['billNo'];
      if (!billNo) return;

      setIsLoading(true);
      try {
        const getBillDetailsFn = shouldShowProfit
          ? BillSummaryReportService.getProfitBillDetails
          : BillSummaryReportService.getBillDetails;
        const res = await getBillDetailsFn({ bill_no: billNo });
        setSaleDetails(res);
      } catch (error) {}
      setIsLoading(false);
    };
    fetchSaleDetails();
  }, [selectedSale, shouldShowProfit]);

  const hasCustomer = saleDetails?.customer != null;
  const paymentModes = (saleDetails?.config?.payment_modes || []).filter(
    (payment_mode) => payment_mode.amount !== 0
  );
  const customerCreditUsed = hasCustomer ? saleDetails?.config?.customer_credit_used : null;
  const loyaltyPointsUsed = hasCustomer ? saleDetails?.config?.loyalty_points_used : null;
  const loyaltyPointsAmount = hasCustomer ? saleDetails?.config?.loyalty_points_amount : null;

  const totalDiscount =
    Number(saleDetails?.discount || 0) + Number(saleDetails?.discount_applied_on_items || 0);

  const showCustomerCredit = customerCreditUsed != null && Number(customerCreditUsed) !== 0;
  const showLoyaltyPointsUsed = loyaltyPointsUsed != null && Number(loyaltyPointsUsed) !== 0;
  const showLoyaltyPointsAmount = loyaltyPointsAmount != null && Number(loyaltyPointsAmount) !== 0;
  const showDiscount = totalDiscount !== 0;

  const hasPaymentDetails =
    paymentModes.length > 0 ||
    showCustomerCredit ||
    showLoyaltyPointsUsed ||
    showLoyaltyPointsAmount ||
    showDiscount;

  return (
    <div className={styles.billPopup}>
      <div className={styles.billContentWrapper}>
        <div className={styles.billHeadSec}>
          <h2 className={styles.billTitle}>{tCommon('Bill Details')}</h2>
          <div className={styles.billCloseBtn} onClick={handleClose}>
            <CloseIcon />
          </div>
        </div>
        <div className={styles.billDetailsWrapper}>
          {isLoading ? (
            <div className={styles.loadingContainer}>
              <Loader isOpen={true} backdropColor={'transparent'} />
            </div>
          ) : (
            <>
              <div className={styles.billMetaBar}>
                <div className={styles.billMetaItem}>
                  <div className={styles.billMetaIconWrapper}>
                    <ReceiptLongIcon className={styles.billMetaIcon} />
                  </div>
                  <div className={styles.billMetaTextGroup}>
                    <span className={styles.billMetaLabel}>{tCommon('Bill No')}</span>
                    <span className={styles.billMetaValue}>{saleDetails?.bill_no || selectedSale?.bill_no || '-'}</span>
                  </div>
                </div>
                <div className={styles.billMetaItemRight}>
                  <div className={styles.billMetaTextGroupRight}>
                    <span className={styles.billMetaLabel}>{tCommon('Date')}</span>
                    <span className={styles.billMetaValue}>
                      {saleDetails?.time
                        ? `${DateTimeHelpers.convertDateToIsoDMY(
                            saleDetails.time
                          )} ${DateTimeHelpers.convertTo12HourTime(saleDetails.time)}`
                        : '-'}
                    </span>
                  </div>
                  <div className={styles.billMetaIconWrapperRight}>
                    <CalendarTodayIcon className={styles.billMetaIcon} />
                  </div>
                </div>
              </div>

              {saleDetails?.customer != null && (
                <div className={styles.billCustomerCard}>
                  <div className={styles.billCustomerInfoGroup}>
                    <span className={styles.billCustomerLabel}>{tCommon('Customer ID')}</span>
                    <h3 className={styles.billCustomerValue}>{saleDetails?.customer.id}</h3>
                  </div>
                  <div className={styles.billCustomerInfoGroup}>
                    <span className={styles.billCustomerLabel}>{tCommon('Name')}</span>
                    <h3 className={styles.billCustomerValue}>{saleDetails?.customer.name}</h3>
                  </div>
                  <div className={styles.billCustomerInfoGroup}>
                    <span className={styles.billCustomerLabel}>{tCommon('Location')}</span>
                    <h3 className={styles.billCustomerValue}>{saleDetails?.customer.location}</h3>
                  </div>
                  <div>
                    <Button
                      variant="contained"
                      onClick={() => redirectToCustomerrDetails(saleDetails?.customer)}
                      className={styles.billCustomerBtn}
                      disableElevation
                    >
                      {tCommon('Customer Details')}
                    </Button>
                  </div>
                </div>
              )}

              {hasPaymentDetails && (
                <div className={styles.billPaymentSection}>
                  <h2 className={styles.billSectionHeader}>{tCommon('Payment Details')}</h2>
                  <div className={styles.billPaymentGrid}>
                    <Grid container spacing={2}>
                      {paymentModes.map((payment_mode, index) => (
                        <Grid item xs={6} md={3} key={`payment-mode-${index}`}>
                          <div className={styles.billPaymentItem}>
                            <span className={styles.billPaymentLabel}>{payment_mode.name}</span>
                            <span className={styles.billPaymentValue}>{payment_mode.amount}</span>
                          </div>
                        </Grid>
                      ))}
                      {showCustomerCredit && (
                        <Grid item xs={6} md={3} key="customer-credit-used">
                          <div className={styles.billPaymentItem}>
                            <span className={styles.billPaymentLabel}>{tCommon('Credit')}</span>
                            <span className={styles.billPaymentValue}>{customerCreditUsed}</span>
                          </div>
                        </Grid>
                      )}
                      {showLoyaltyPointsAmount && (
                        <Grid item xs={6} md={3} key="loyalty-points-amount">
                          <div className={styles.billPaymentItem}>
                            <span className={styles.billPaymentLabel}>{tCommon('Points')}</span>
                            <span className={styles.billPaymentValue}>{loyaltyPointsAmount}</span>
                          </div>
                        </Grid>
                      )}
                      {showDiscount && (
                        <Grid item xs={6} md={3} key="discount">
                          <div className={styles.billPaymentItem}>
                            <span className={styles.billPaymentLabel}>{tCommon('Discount')}</span>
                            <span className={styles.billPaymentValue}>{totalDiscount}</span>
                          </div>
                        </Grid>
                      )}
                    </Grid>
                    {showLoyaltyPointsUsed && (
                      <div className={styles.billLoyaltyNote}>
                        <span>
                          * {tCommon('Loyalty Points Used', 'Loyalty Points Used')}: {loyaltyPointsUsed}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {saleDetails?.remark && (
                <div className={styles.billRemarkBox}>
                  <div className={styles.billRemarkLabel}>{tCommon('Remark')}</div>
                  <p className={styles.billRemarkValue}>{saleDetails?.remark}</p>
                </div>
              )}

              <div className={styles.billTableContainer}>
                {saleDetails?.items ? (
                  <>
                    <DataTable
                      columns={tableHeaders}
                      rows={saleDetails?.items}
                      summary={{
                        'item.plu': tCommon('Total'),
                        rate: `${shop?.currency || ''} ${summary.totalPrice}`,
                        tax: `${shop?.currency || ''} ${summary.totalTax}`,
                        payable: `${shop?.currency || ''} ${summary.totalAmount}`,
                        profit: shouldShowProfit ? `${shop?.currency || ''} ${summary.totalProfit}` : undefined,
                      }}
                    />
                    <div className={styles.billTableFooterSummary}>
                      <div className={styles.billTableFooterGroup}>
                        {(totalDiscount || 0) > 0 && (
                          <div className={styles.billTableFooterRow}>
                            <span className={styles.billTableFooterLabel}>
                              {tCommon('Discount')}:
                            </span>
                            <span className={styles.billTableFooterValue}>
                              {shop?.currency || ''}{' '}
                              {ShopsHelper.getAmountFormatted(shop, totalDiscount)}
                            </span>
                          </div>
                        )}
                        <div className={`${styles.billTableFooterRow} ${styles.billTableFooterPayableRow}`}>
                          <span className={styles.billTableFooterPayableLabel}>
                            {tCommon('Payable')}:
                          </span>
                          <span className={styles.billTableFooterPayableValue}>
                            {shop?.currency || ''}{' '}
                            {ShopsHelper.getAmountFormatted(
                              shop,
                              saleDetails?.payable
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <Info
                    title={tPopup('Bill Details not found', 'Bill Details not found')}
                    content={tPopup('This bill have no items to list up')}
                  />
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
